import posthog from './posthog';

// PostHog only runs for visitors who accepted analytics (AnalyticsGate); for
// everyone else these are no-ops, never errors
const ready = () => typeof window !== 'undefined' && posthog && posthog.__loaded;

/**
 * The replay this browser is recording, so events the API records for this
 * person (a refusal, a payment) can be watched in the session they happened in.
 * Empty for a visitor who declined analytics, which is also the right answer.
 */
export function currentSessionId() {
  try {
    return ready() && typeof posthog.get_session_id === 'function' ? posthog.get_session_id() : null;
  } catch {
    return null;
  }
}

export function track(event, properties = {}) {
  try {
    if (ready()) posthog.capture(event, properties);
  } catch {
    // Analytics never breaks the page
  }
}

// Sessions on a phone and a laptop become one person. The app's own user id
// only: no email or name goes to PostHog.
export function identifyUser(user) {
  try {
    if (ready() && user?.id != null) posthog.identify(String(user.id));
  } catch {
    // Analytics never breaks the page
  }
}

export function forgetUser() {
  try {
    if (ready()) posthog.reset();
  } catch {
    // Analytics never breaks the page
  }
}

const ID = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i;

// /projects/2b1c…/shorts/9f0e…/extract → /projects/:id/shorts/:id/extract, and
// no query string (it can carry a video URL)
export const routeOf = (url = '') => {
  const path = String(url).split('?')[0].replace(/^https?:\/\/[^/]+/, '');
  return path.split('/').map((part) => (ID.test(part) ? ':id' : part)).join('/');
};

// The API calls that are product moments, by method and route:
// [event when it worked, event when it did not, properties]
const MOMENTS = {
  'post /projects': ['project_created', 'project_create_failed', { tool: 'ai-clipping' }],
  'post /projects/transcription': ['project_created', 'project_create_failed', { tool: 'auto-caption' }],
  'post /projects/reframe': ['project_created', 'project_create_failed', { tool: 'reframe' }],
  'post /ai-agent/projects': ['project_created', 'project_create_failed', { tool: 'ai-agent' }],
  'post /projects/:id/retry': ['project_retried', 'project_retry_failed', {}],
  'post /projects/:id/shorts/:id/extract': ['clip_export_started', 'clip_export_failed', {}],
  'post /ai-agent/projects/:id/render': ['ai_video_render_started', 'ai_video_render_failed', {}],
  // Turning on a public link (and turning it off again) for WhatsApp or a copied link
  'post /ai-agent/projects/:id/share': ['ai_video_share_toggled', 'ai_video_share_failed', {}],
  // Remix: an AI-clipping clip wrapped in a narrated AI Agent video (routes/remix.py)
  'post /ai-agent/remix': ['project_created', 'project_create_failed', { tool: 'remix' }],
  // A scene clip: one animated scene with sound, from the same idea box (routes/scene.py)
  'post /ai-agent/scenes': ['project_created', 'project_create_failed', { tool: 'scene' }],
  'put /ai-agent/remix/:id/hook': ['remix_title_card_edited', 'remix_title_card_edit_failed', {}],
  'put /ai-agent/remix/:id/scenes/:id/callouts': ['remix_notes_edited', 'remix_notes_edit_failed', {}],
  // The full editor's Export (routes/render.py), from whichever way it was opened
  'post /render/editor': ['editor_export_started', 'editor_export_failed', {}],
};

// What a request itself may add, by route: only choices from a fixed list,
// never what someone typed (a remix's direction, a title card's words)
const FROM_BODY = {
  'post /ai-agent/remix': (body) => ({
    remix_preset: body.preset,
    remix_commentary: body.commentary,
    remix_has_direction: !!String(body.instructions || '').trim(),
    language: body.language,
  }),
  'post /ai-agent/projects': (body) => ({ language: body.language, duration: body.duration, aspect_ratio: body.aspect_ratio }),
  'post /ai-agent/scenes': (body) => ({ language: body.language, aspect_ratio: body.aspect_ratio, scene_source: body.source, scene_shape: body.shape }),
  // The shape and length of the export and how many layers it has: never the
  // layers themselves, which carry what someone typed
  'post /render/editor': (body) => ({
    width: body.width,
    height: body.height,
    seconds: Math.round(body.duration_in_frames / (body.fps || 30)),
    layers: Array.isArray(body.overlays) ? body.overlays.length : undefined,
  }),
};

// The ids routeOf turned into :id, in the order they appear. Our own
// identifiers — the same ones already sitting in $current_url — never anything
// a person typed.
const idsIn = (url = '') =>
  String(url).split('?')[0].replace(/^https?:\/\/[^/]+/, '').split('/').filter((part) => ID.test(part));

// Which project or clip a moment was about. `clip_export_started` carried
// nothing at all, so a session with ten of them could not be read: someone
// working down a list of ten clips and someone tapping one clip ten times
// because nothing seemed to happen look identical, and they mean opposite
// things.
const FROM_URL = {
  'post /projects/:id/shorts/:id/extract': ([project, clip]) => ({ project_id: project, clip_id: clip }),
  'post /projects/:id/shorts/:id/render': ([project, clip]) => ({ project_id: project, clip_id: clip }),
  'post /projects/:id/retry': ([project]) => ({ project_id: project }),
  'post /ai-agent/projects/:id/render': ([project]) => ({ project_id: project }),
  'post /ai-agent/projects/:id/share': ([project]) => ({ project_id: project }),
  'put /ai-agent/remix/:id/hook': ([project]) => ({ project_id: project }),
  'put /ai-agent/remix/:id/scenes/:id/callouts': ([project]) => ({ project_id: project }),
};

const parse = (data) => {
  if (!data) return {};
  if (typeof data === 'object') return data;
  try { return JSON.parse(data); } catch { return {}; }
};

/**
 * What one API response says about the product, as [event, properties], or
 * null. A moment that was refused or failed carries the status (403 is a plan
 * limit); any other call the server fails (5xx, or no answer) is `api_error`,
 * with axios's error code (ERR_NETWORK, ECONNABORTED...). A request the app
 * cancelled itself (a poll stopped by leaving the page) says nothing: they were
 * most of the `api_error`s with status 0.
 */
export function apiEventFor(method = 'get', url = '', status = 0, data = null, code = undefined) {
  if (code === 'ERR_CANCELED') return null;
  const route = routeOf(url);
  const key = `${String(method).toLowerCase()} ${route}`;
  const moment = MOMENTS[key];
  const ok = status >= 200 && status < 300;
  if (moment) {
    const [worked, failed, base] = moment;
    let properties = { ...base };
    if (FROM_URL[key]) {
      try { properties = { ...properties, ...FROM_URL[key](idsIn(url)) }; } catch { /* the event still goes */ }
    }
    if (FROM_BODY[key]) {
      try { properties = { ...properties, ...FROM_BODY[key](parse(data)) }; } catch { /* the event still goes */ }
    }
    return ok ? [worked, properties] : [failed, { ...properties, status }];
  }
  if (!ok && (status === 0 || status >= 500)) {
    return ['api_error', { status, route, method: String(method).toLowerCase(), ...(code ? { code } : {}) }];
  }
  return null;
}
